import React, { useState, useEffect } from 'react';
import { kakoAPI, applicationsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const InscricaoAfiliado = () => {
  const [step, setStep] = useState(1); // 1: escolha tipo, 2: validar KAKO, 3: REGRAS, 4: formulário
  const [type, setType] = useState(null); // "HOST" ou "AGENTE"
  const [kakoId, setKakoId] = useState('');
  const [kakoData, setKakoData] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ host_active: false, agente_active: false });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Novo
  const [showErrorModal, setShowErrorModal] = useState(false); // Novo
  const [errorMessage, setErrorMessage] = useState(''); // Novo
  
  const [formData, setFormData] = useState({
    password: '',
    followers_count: '',
    agency: '',
    agency_id: '',
    host_count: '',
    whatsapp_country: '+55', // NOVO
    whatsapp_number: '' // NOVO
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await applicationsAPI.getConfig();
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const handleTypeSelection = (selectedType) => {
    // Verifica se inscrições estão ativas
    if (selectedType === 'HOST' && !config.host_active) {
      toast.error('Inscrições para HOST estão temporariamente desativadas');
      return;
    }
    if (selectedType === 'AGENTE' && !config.agente_active) {
      toast.error('Inscrições para AGENTE estão temporariamente desativadas');
      return;
    }
    
    setType(selectedType);
    setStep(2);
  };

  const handleVerifyKako = async () => {
    if (!kakoId || kakoId.length < 4) {
      toast.error('Insira um KAKO ID válido');
      return;
    }

    setLoading(true);
    try {
      // Verifica status da aplicação
      const statusRes = await applicationsAPI.checkStatus(kakoId);
      
      if (statusRes.data.exists) {
        setApplicationStatus(statusRes.data);
        
        if (statusRes.data.status === 'approved') {
          toast.success(statusRes.data.message);
        } else if (statusRes.data.status === 'pending') {
          toast.info(statusRes.data.message);
        } else if (statusRes.data.status === 'rejected') {
          toast.error(statusRes.data.message);
        }
        return;
      }

      // Verifica usuário KAKO
      const kakoRes = await kakoAPI.verifyUser(kakoId);
      console.log('Resposta completa KAKO:', kakoRes.data);
      
      // Os dados estão em kakoRes.data.user
      const userData = kakoRes.data.user;
      console.log('Dados do usuário:', userData);
      
      setKakoData(userData);
      setStep(3); // Vai para tela de REGRAS
      toast.success(`KAKO ID verificado! Bem-vindo, ${userData.nickname}`);
    } catch (error) {
      console.error('Erro ao verificar KAKO:', error.response?.data);
      
      let errorMessage = 'Erro ao verificar KAKO ID';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.password || formData.password.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    // Checkbox já foi aceito no step 3, não precisa validar aqui

    if (type === 'HOST') {
      if (!formData.followers_count || parseInt(formData.followers_count) < 2000) {
        toast.error('HOSTs devem ter no mínimo 2.000 seguidores');
        return;
      }
      if (!formData.agency) {
        toast.error('Informe o nome da agência');
        return;
      }
    }

    if (type === 'AGENTE') {
      if (!formData.agency_id) {
        toast.error('Informe o ID da agência');
        return;
      }
      if (!formData.host_count || parseInt(formData.host_count) < 1) {
        toast.error('Informe o número de hosts');
        return;
      }
    }

    setLoading(true);
    try {
      // Validação de números
      let followersCount = null;
      let hostCount = null;

      if (type === 'HOST') {
        followersCount = parseInt(formData.followers_count);
        if (isNaN(followersCount)) {
          toast.error('Número de seguidores inválido');
          setLoading(false);
          return;
        }
      }

      if (type === 'AGENTE') {
        hostCount = parseInt(formData.host_count);
        if (isNaN(hostCount)) {
          toast.error('Número de hosts inválido');
          setLoading(false);
          return;
        }
      }

      const data = {
        kako_id: kakoId,
        nickname: kakoData.nickname,
        kako_avatar: kakoData.avatar || '',
        type: type,
        password: formData.password,
        whatsapp: formData.whatsapp_country + formData.whatsapp_number, // Combina código + número
        ...(type === 'HOST' && {
          followers_count: followersCount,
          agency: formData.agency
        }),
        ...(type === 'AGENTE' && {
          agency_id: formData.agency_id,
          host_count: hostCount
        })
      };

      await applicationsAPI.create(data);
      
      // Mostra modal de sucesso ao invés de toast
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Erro ao criar aplicação:', error.response?.data);
      
      // Trata erro de validação (422)
      let errorMsg = 'Erro ao enviar solicitação';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pega a primeira mensagem de erro de validação
          errorMsg = detail[0]?.msg || detail[0]?.message || 'Dados inválidos';
        }
      }
      
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-pink-600 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🦊 FOX SELLER</h1>
          <p className="text-purple-200">Inscrição de Afiliados</p>
        </div>

        {/* Step 1: Escolha do tipo */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Escolha seu tipo de afiliação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={() => handleTypeSelection('HOST')}
                disabled={!config.host_active}
                className="w-full h-20 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-left w-full">
                  <div className="text-xl font-bold">HOST</div>
                  <div className="text-sm opacity-90">Streamer / Criador de Conteúdo</div>
                  {!config.host_active && (
                    <div className="text-xs mt-1 bg-red-500 px-2 py-1 rounded inline-block">
                      🔒 Inscrições Fechadas
                    </div>
                  )}
                </div>
              </Button>
              
              <Button
                onClick={() => handleTypeSelection('AGENTE')}
                disabled={!config.agente_active}
                className="w-full h-20 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="text-left w-full">
                  <div className="text-xl font-bold">AGENTE</div>
                  <div className="text-sm opacity-90">Representante de Agência</div>
                  {!config.agente_active && (
                    <div className="text-xs mt-1 bg-red-500 px-2 py-1 rounded inline-block">
                      🔒 Inscrições Fechadas
                    </div>
                  )}
                </div>
              </Button>

              {!config.host_active && !config.agente_active && (
                <p className="text-center text-sm text-gray-600 mt-4">
                  ⚠️ Inscrições temporariamente fechadas. Volte em breve!
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Validar KAKO ID */}
        {step === 2 && !applicationStatus && (
          <Card>
            <CardHeader>
              <CardTitle>
                {type === 'HOST' ? '🎭 Inscrição HOST' : '💼 Inscrição AGENTE'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>KAKO ID</Label>
                <Input
                  type="text"
                  value={kakoId}
                  onChange={(e) => setKakoId(e.target.value)}
                  placeholder="Seu KAKO ID"
                  maxLength={8}
                />
              </div>

              <Button
                onClick={handleVerifyKako}
                disabled={loading}
                className="w-full bg-purple-600"
              >
                {loading ? 'Verificando...' : 'Verificar KAKO ID'}
              </Button>

              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="w-full"
              >
                Voltar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Status da aplicação */}
        {applicationStatus && (
          <Card>
            <CardContent className="p-6">
              {applicationStatus.status === 'pending' && (
                <div className="text-center space-y-4">
                  <Clock className="h-16 w-16 mx-auto text-yellow-500" />
                  <h3 className="text-xl font-bold">Solicitação em Análise</h3>
                  <p className="text-gray-600">{applicationStatus.message}</p>
                </div>
              )}

              {applicationStatus.status === 'approved' && (
                <div className="text-center space-y-4">
                  <CheckCircle2 className="h-16 w-16 mx-auto text-green-500" />
                  <h3 className="text-xl font-bold">Aprovado!</h3>
                  <p className="text-gray-600">{applicationStatus.message}</p>
                  <Button
                    onClick={() => window.location.href = '/reseller/login'}
                    className="bg-green-600"
                  >
                    Fazer Login
                  </Button>
                </div>
              )}

              {applicationStatus.status === 'rejected' && (
                <div className="space-y-4">
                  <div className="text-center">
                    <XCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
                    <h3 className="text-xl font-bold text-red-600">Solicitação Recusada</h3>
                  </div>
                  
                  {/* Mensagem formatada */}
                  <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-left whitespace-pre-line">
                    {applicationStatus.message}
                  </div>
                </div>
              )}

              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full mt-4"
              >
                Voltar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: REGRAS - NOVO */}
        {step === 3 && kakoData && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-center">
                ⚖️ REGRAS DE CONDUTA E ELEGIBILIDADE
              </CardTitle>
              <p className="text-sm text-center text-gray-600">
                Leia atentamente antes de prosseguir
              </p>
            </CardHeader>
            <CardContent>
              {/* REGRAS */}
              <Card className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 border-orange-300">
                <CardContent className="p-4 max-h-96 overflow-y-auto text-sm space-y-4">
                  <p className="font-semibold text-purple-900">
                    A FOX SELLER é o programa oficial de afiliação destinado a profissionais do ecossistema KAKO LIVE que desejam crescer de forma ética, transparente e sustentável.
                  </p>
                  
                  {type === 'HOST' && (
                    <div className="space-y-3">
                      <h4 className="font-bold text-purple-900">🎙️ HOSTS AFILIADOS — REGRAS E CRITÉRIOS</h4>
                      
                      <div>
                        <p className="font-semibold">1. Elegibilidade</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>Deve possuir mínimo de 2.000 seguidores reais na plataforma KAKO LIVE.</li>
                          <li>Não pode ser membro ativo de nenhuma outra agência, incluindo agências afiliadas à FOX SELLER.</li>
                          <li>É terminantemente proibida a promoção, divulgação ou vínculo com qualquer outro coin seller além da FOX SELLER.</li>
                          <li>Deve manter boa imagem pública e conduta exemplar dentro e fora da plataforma.</li>
                          <li>É obrigatório seguir as políticas de boa conduta da FOX SELLER e todas as diretrizes oficiais da KAKO LIVE.</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-semibold">2. Conduta Profissional</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>Manter respeito, ética e cordialidade com colegas, agentes e espectadores.</li>
                          <li>Cumprir horários, metas e compromissos acordados com a gestão FOX.</li>
                          <li>Evitar conteúdos inadequados, ofensivos ou contrários às políticas da plataforma.</li>
                          <li>Não divulgar informações internas, valores, estratégias ou contatos de terceiros.</li>
                          <li>Utilizar apenas canais oficiais FOX SELLER para comunicações relacionadas à afiliação.</li>
                          <li>Representar a marca FOX SELLER com profissionalismo, responsabilidade e lealdade.</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-semibold">3. Ética e Penalidades</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>É proibido mentir, difamar, manipular resultados ou prometer benefícios falsos.</li>
                          <li>O descumprimento das regras resultará em advertência, suspensão ou desligamento imediato, com bloqueio de benefícios e comissões pendentes.</li>
                          <li>Casos graves implicam em banimento definitivo do programa de afiliação.</li>
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {type === 'AGENTE' && (
                    <div className="space-y-3">
                      <h4 className="font-bold text-purple-900">💼 AGENTES AFILIADOS — REGRAS E CONDUTA</h4>
                      
                      <div>
                        <p className="font-semibold">1. Responsabilidades e Elegibilidade</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>O agente deve seguir e aplicar todas as políticas oficiais da FOX SELLER junto aos hosts sob sua gestão.</li>
                          <li>É responsável por orientar, supervisionar e garantir o cumprimento das regras.</li>
                          <li>É proibido promover, divulgar ou manter vínculo com qualquer coin seller além da FOX SELLER.</li>
                          <li>É vedada a participação em agências concorrentes ou afiliadas à FOX SELLER, direta ou indiretamente.</li>
                          <li>Deve atuar com transparência e profissionalismo absoluto em todos os processos.</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-semibold">2. Conduta Profissional</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>Agir com sigilo, ética e respeito nas interações internas e externas.</li>
                          <li>Evitar conflitos de interesse, disputas desleais ou qualquer ato que prejudique a imagem da FOX SELLER.</li>
                          <li>Garantir que os hosts sob sua supervisão sigam integralmente as normas.</li>
                          <li>É proibido oferecer promessas, bônus ou vantagens não autorizadas pela administração FOX.</li>
                          <li>Todo material promocional deve ser pré-aprovado pela equipe oficial.</li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-semibold">3. Ética e Penalidades</p>
                        <ul className="list-disc ml-5 space-y-1">
                          <li>É proibido o uso indevido da marca, desvio de afiliados, repasse de informações sigilosas ou concorrência desleal.</li>
                          <li>O descumprimento das regras resultará em desligamento imediato e cancelamento de comissões pendentes.</li>
                          <li>Falhas graves serão tratadas como violação contratual, com bloqueio definitivo de vínculo com a FOX SELLER.</li>
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2 bg-purple-100 p-3 rounded">
                    <h4 className="font-bold text-purple-900">⚖️ POLÍTICA GERAL DE BOA CONDUTA (APLICÁVEL A TODOS)</h4>
                    <p>Todos os afiliados da FOX SELLER devem:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Agir sempre com ética, respeito e profissionalismo.</li>
                      <li>Evitar comportamentos ofensivos, preconceituosos ou desrespeitosos dentro ou fora da plataforma.</li>
                      <li>Cumprir todas as diretrizes do KAKO LIVE e da FOX SELLER.</li>
                      <li>Manter lealdade exclusiva à FOX SELLER como coin seller oficial.</li>
                      <li>Não se vincular, representar ou divulgar qualquer agência concorrente, mesmo que afiliada à FOX SELLER.</li>
                      <li>Zelar pela imagem, reputação e credibilidade da marca em todas as interações.</li>
                    </ul>
                    <p className="font-bold text-center text-purple-900 mt-3">
                      🦊 Valores Inegociáveis: ÉTICA · TRANSPARÊNCIA · PROFISSIONALISMO · LEALDADE
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Checkbox de Aceite */}
              <div className="flex items-start gap-3 p-4 bg-white rounded border-2 border-purple-300">
                <Checkbox
                  checked={acceptedTerms}
                  onCheckedChange={setAcceptedTerms}
                  className="mt-1"
                />
                <label className="text-sm font-semibold">
                  Li e aceito todas as regras de conduta e me comprometo a seguir as políticas de afiliação da FOX SELLER
                </label>
              </div>

              {/* Botões */}
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => setStep(2)}
                  variant="outline"
                  className="flex-1"
                >
                  Voltar
                </Button>
                <Button
                  onClick={() => {
                    if (!acceptedTerms) {
                      toast.error('Você deve aceitar as regras para continuar');
                      return;
                    }
                    setStep(4);
                  }}
                  disabled={!acceptedTerms}
                  className="flex-1 bg-purple-600"
                >
                  Prosseguir para Formulário
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Formulário - MOVIDO */}
        {step === 4 && kakoData && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>
                {type === 'HOST' ? '🎭 Formulário HOST' : '💼 Formulário AGENTE'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Dados do KAKO - Card Destacado */}
                <Card className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300">
                  <CardContent className="p-4">
                    <p className="text-xs text-purple-600 font-semibold mb-2">SEUS DADOS</p>
                    <div className="flex items-center gap-4">
                      {/* Avatar - sempre tenta exibir */}
                      <div className="w-16 h-16 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden border-2 border-purple-400">
                        {kakoData?.avatar ? (
                          <img 
                            src={kakoData.avatar} 
                            alt={kakoData?.nickname || 'Avatar'} 
                            className="w-full h-full object-cover" 
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<span class="text-2xl">👤</span>';
                            }}
                          />
                        ) : (
                          <span className="text-3xl">👤</span>
                        )}
                      </div>
                      
                      {/* Nickname e KAKO ID */}
                      <div className="flex-1">
                        <div className="text-lg font-bold text-purple-900">
                          {kakoData?.nickname || 'Usuário'}
                        </div>
                        <div className="text-sm text-purple-600 font-mono">
                          KAKO ID: {kakoId}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Senha */}
                <div>
                  <Label>Senha para Painel de Afiliado</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ⚠️ Crie uma senha nova. Por segurança, não use a mesma do app KAKO.
                  </p>
                </div>

                {/* Campos HOST */}
                {type === 'HOST' && (
                  <>
                    <div>
                      <Label>Contato WhatsApp</Label>
                      <div className="flex gap-2">
                        <select
                          className="border rounded px-3 py-2 w-24"
                          value={formData.whatsapp_country}
                          onChange={(e) => setFormData({...formData, whatsapp_country: e.target.value})}
                        >
                          <option value="+55">🇧🇷 +55</option>
                          <option value="+1">🇺🇸 +1</option>
                          <option value="+351">🇵🇹 +351</option>
                          <option value="+34">🇪🇸 +34</option>
                          <option value="+44">🇬🇧 +44</option>
                        </select>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formData.whatsapp_number}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFormData({...formData, whatsapp_number: value});
                          }}
                          placeholder="Ex: 11987654321"
                          className="flex-1"
                          required
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Número com DDD (sem espaços ou caracteres especiais)
                      </p>
                    </div>
                    
                    <div>
                      <Label>Número Atual de Seguidores</Label>
                      <Input
                        type="number"
                        value={formData.followers_count}
                        onChange={(e) => setFormData({...formData, followers_count: e.target.value})}
                        placeholder="Mínimo 2.000"
                        min={2000}
                        required
                      />
                    </div>

                    <div>
                      <Label>Agência</Label>
                      <Input
                        type="text"
                        value={formData.agency}
                        onChange={(e) => setFormData({...formData, agency: e.target.value})}
                        placeholder="Nome da sua agência"
                        required
                      />
                    </div>
                  </>
                )}

                {/* Campos AGENTE */}
                {type === 'AGENTE' && (
                  <>
                    <div>
                      <Label>Contato WhatsApp</Label>
                      <div className="flex gap-2">
                        <select
                          className="border rounded px-3 py-2 w-24"
                          value={formData.whatsapp_country}
                          onChange={(e) => setFormData({...formData, whatsapp_country: e.target.value})}
                        >
                          <option value="+55">🇧🇷 +55</option>
                          <option value="+1">🇺🇸 +1</option>
                          <option value="+351">🇵🇹 +351</option>
                          <option value="+34">🇪🇸 +34</option>
                          <option value="+44">🇬🇧 +44</option>
                        </select>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={formData.whatsapp_number}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFormData({...formData, whatsapp_number: value});
                          }}
                          placeholder="Ex: 11987654321"
                          className="flex-1"
                          required
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Número com DDD (sem espaços ou caracteres especiais)
                      </p>
                    </div>
                    
                    <div>
                      <Label>ID de Agência (6 dígitos)</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={formData.agency_id}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData({...formData, agency_id: value});
                        }}
                        placeholder="Ex: 123456"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Digite exatamente 6 números
                      </p>
                    </div>

                    <div>
                      <Label>Número de Hosts</Label>
                      <Input
                        type="number"
                        value={formData.host_count}
                        onChange={(e) => setFormData({...formData, host_count: e.target.value})}
                        placeholder="Quantidade de hosts"
                        min={1}
                        required
                      />
                    </div>
                  </>
                )}

                {/* Sem checkbox aqui - já aceitou no step anterior */}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600"
                >
                  {loading ? 'Enviando...' : 'Enviar Solicitação'}
                </Button>

                <Button
                  type="button"
                  onClick={() => setStep(3)}
                  variant="outline"
                  className="w-full"
                >
                  Voltar
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Modal de Sucesso */}
        <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
          <DialogContent className="max-w-md">
            <div className="text-center space-y-4 py-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-600">Solicitação Enviada!</h2>
              <p className="text-gray-600">
                Sua solicitação de afiliação foi enviada com sucesso e está em análise.
              </p>
              <p className="text-sm text-gray-500">
                Aguarde a aprovação do administrador. Você pode consultar o status a qualquer momento validando seu KAKO ID nesta página.
              </p>
              <Button
                onClick={() => window.location.reload()}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                OK, Entendi
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Erro */}
        <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
          <DialogContent className="max-w-md">
            <div className="text-center space-y-4 py-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-600">Erro ao Enviar</h2>
              <p className="text-gray-700 font-medium">
                {errorMessage}
              </p>
              <Button
                onClick={() => setShowErrorModal(false)}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                Tentar Novamente
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default InscricaoAfiliado;
